/**
 * WhatsApp-Stil Chat-Bubble für den Thread im Team-/Mandanten-Portal.
 * Extrahiert aus TeamPortal.jsx → wiederverwendbar.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { VertraulichBadge } from './chat/Vertraulich';
import { Star, Paperclip, Trash2, Eye, EyeOff, Download, FileText, Check, CheckCheck, FolderInput, Mail, MailOpen } from 'lucide-react';

const isImage = (ct) => (ct || '').startsWith('image/');
const isPdf = (ct) => ct === 'application/pdf';

const ImageThumb = ({ att, onLoad, onClick }) => {
  const [url, setUrl] = useState('');
  useEffect(() => {
    let alive = true; let obj = '';
    onLoad?.(att).then((u) => { if (alive) { obj = u; setUrl(u); } else URL.revokeObjectURL(u); }).catch(() => {});
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
  }, [att, onLoad]);
  return (
    <button type="button" onClick={onClick} className="block mt-1.5 rounded-lg overflow-hidden bg-black/5 max-w-[260px]" data-testid={`attachment-thumb-${att.gridfs_id}`} title="Vorschau öffnen">
      {url ? <img src={url} alt={att.filename} className="max-h-56 w-auto object-contain" /> : <div className="w-40 h-28 animate-pulse bg-black/10" />}
    </button>
  );
};

const TEAM_ROLES = ['admin', 'kanzlei_mitarbeiter', 'freelancer'];

const fmtDateTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: 'short', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };

export const Haekchen = ({ read, zugestellt, mine, wer, testId }) => {
  const stufe = read ? 'gelesen' : zugestellt ? 'zugestellt' : 'gesendet';
  const grau = mine ? 'text-white/60' : 'text-[#34536b]/60';
  const title = read ? `Von ${wer} gelesen am ${fmtDateTime(read)}` : zugestellt ? `Bei ${wer} zugestellt am ${fmtDateTime(zugestellt)} – noch nicht gelesen` : 'Gesendet – noch nicht zugestellt';
  return (
    <span className={`inline-flex items-center gap-0.5 ${read ? `font-medium ${mine ? 'text-sky-300' : 'text-sky-600'}` : grau}`} title={title} data-testid={testId} data-read={read ? 'true' : 'false'} data-stufe={stufe}>
      {stufe === 'gesendet' ? <Check className="w-3.5 h-3.5" /> : <CheckCheck className="w-3.5 h-3.5" />}{read && <> Gelesen {fmtTime(read)}</>}
    </span>
  );
};

const ReadReceipt = ({ item, mine, isTeam }) => (isTeam
  ? <Haekchen read={item.client_read_at} zugestellt={item.client_zugestellt_at} mine={mine} wer="Mandanten" testId={`read-receipt-${item.id}`} />
  : <Haekchen read={item.team_gelesen ? (item.team_gelesen_am || item.created_at) : null} zugestellt={item.team_zugestellt_at} mine={mine} wer="der Kanzlei" testId={`read-receipt-${item.id}`} />);

export const MessageBubble = ({
  item,
  currentUserId,
  isTeam,
  isAdmin = isTeam,
  onToggleStar,
  onDownloadAttachment,
  onPreviewAttachment,
  onLoadThumb,
  onDelete,
  onHide,
  onUnhide,
  onMove,
  onToggleUngelesen,
  label,
}) => {
  const mine = item.author_user_id === currentUserId;
  const fromTeam = TEAM_ROLES.includes(item.author_role);

  const bubbleCls = mine
    ? 'bg-[#1B3A5C] text-white rounded-2xl rounded-br-md'
    : fromTeam
      ? 'bg-[#fdf3e0] border border-[#b8893d]/25 text-[#1B3A5C] rounded-2xl rounded-bl-md'
      : 'bg-white border border-gray-200 text-[#1B3A5C] rounded-2xl rounded-bl-md';

  const handleDelete = () => {
    if (!window.confirm('Diese Nachricht wirklich löschen?')) return;
    onDelete?.(item.id);
  };

  return (
    <div
      className={`flex ${mine ? 'justify-end' : 'justify-start'} mb-1.5 ${item.hidden ? 'opacity-50' : ''}`}
      data-testid={`msg-${item.id}`}
    >
      <div
        className={`max-w-[78%] px-3.5 py-2 relative shadow-sm ${bubbleCls} ${item._highlight ? 'ring-2 ring-[#B8893D]' : ''} ${
          item.starred ? 'ring-2 ring-amber-400 ring-offset-1' : ''
        }`}
      >
        {item.starred && (
          <Star
            className="w-3.5 h-3.5 fill-amber-400 text-amber-500 absolute -top-2 -right-2 bg-white rounded-full p-0.5 shadow-sm"
            data-testid={`star-indicator-${item.id}`}
          />
        )}
        {item.vertraulich_fuer && <VertraulichBadge item={item} isTeam={isTeam} mine={mine} />}
        {label && (
          <span className={`inline-block text-[9px] tracking-wider font-mono px-1.5 py-0.5 rounded-sm mb-1 ${mine ? 'bg-white/15 text-white/90' : 'bg-[#1B3A5C]/8 text-[#1B3A5C]'}`} data-testid={`msg-thread-label-${item.id}`}>
            {label}
          </span>
        )}
        {item.moved_from && (
          <div className={`flex items-center gap-1 text-[9.5px] italic mb-0.5 ${mine ? 'text-white/60' : 'text-[#34536b]/60'}`} data-testid={`msg-moved-${item.id}`}
            title={item.moved_at ? `Verschoben ${fmtDateTime(item.moved_at)}${item.moved_by ? ` von ${item.moved_by}` : ''}` : undefined}>
            <FolderInput className="w-3 h-3" /> verschoben aus {item.moved_from}
          </div>
        )}
        {!mine && (
          <div className={`text-[10.5px] font-semibold ${fromTeam ? 'text-[#b8893d]' : 'text-[#1B3A5C]/80'} mb-0.5`}>
            {fromTeam && !isTeam ? 'Kanzlei Nabhan Gäbler & Partner' : `${item.author_name}${fromTeam ? ' · Kanzlei' : ''}`}
          </div>
        )}
        {isTeam && item.herkunft_info && <p className={`text-[10px] mb-1 break-words ${mine ? 'text-white/75' : 'text-[#56616D]'}`} data-testid={`msg-herkunft-${item.id}`}>
          {item.herkunft_info.richtung} · {item.herkunft_info.kanal} · {item.herkunft_info.von}
        </p>}
        {item.text && (
          <p className={`text-[13.5px] leading-snug whitespace-pre-wrap ${mine ? 'text-white' : 'text-[#1B3A5C]'}`}>
            {String(item.text).split(/(\/portal\/[A-Za-z0-9_\-/]+)/g).map((t, i) => (i % 2
              ? <Link key={i} to={t} className={`underline font-medium ${mine ? 'text-[#e0b56a]' : 'text-[#b8893d]'}`} data-testid={`msg-portal-link-${item.id}-${i}`}>{t.startsWith('/portal/angaben/') ? 'Angaben jetzt ergänzen' : t}</Link>
              : t))}
          </p>
        )}
        {item.unterlagen_ids?.length > 0 && <Link to={isTeam ? `/kanzlei/akten/${item.unterlagen_case_id}?reiter=uebersicht` : `/portal/akten/${item.unterlagen_case_id}#unterlagen`} className={`block text-xs underline mt-2 ${mine ? 'text-[#e0b56a]' : 'text-[#1B3A5C]'}`} data-testid={`msg-checkliste-${item.id}`}>{item.unterlagen_ids.length} angeforderte Unterlagen · Checkliste</Link>}
        {item.attachment && (() => {
          const att = item.attachment;
          const previewable = isImage(att.mime) || isPdf(att.mime);
          const accent = mine ? 'text-[#e0b56a]' : 'text-[#b8893d]';
          return (
            <div data-testid={`attachment-${att.gridfs_id}`}>
              {isImage(att.mime) && onLoadThumb && <ImageThumb att={att} onLoad={onLoadThumb} onClick={() => onPreviewAttachment?.(att)} />}
              <div className={`flex items-center gap-2 mt-1.5 text-[11.5px] ${accent}`}>
                {isPdf(att.mime) ? <FileText className="w-3.5 h-3.5 flex-shrink-0" /> : <Paperclip className="w-3 h-3 flex-shrink-0" />}
                <span className="truncate max-w-[200px]" title={att.filename}>{att.filename}</span>
                <span className="opacity-70 flex-shrink-0">({Math.max(1, Math.round((att.size || 0) / 1024))} KB)</span>
                {previewable && (
                  <button type="button" onClick={() => onPreviewAttachment?.(att)} title="Vorschau" aria-label="Vorschau"
                    data-testid={`attachment-preview-${att.gridfs_id}`} className="p-1 rounded hover:bg-black/10 flex-shrink-0"><Eye className="w-3.5 h-3.5" /></button>
                )}
                <button type="button" onClick={() => onDownloadAttachment?.(att)} title="Herunterladen" aria-label="Herunterladen"
                  data-testid={`attachment-download-${att.gridfs_id}`} className="p-1 rounded hover:bg-black/10 flex-shrink-0"><Download className="w-3.5 h-3.5" /></button>
              </div>
              {isTeam && fromTeam && att.geoeffnet_am && <p className={`mt-0.5 text-[10px] ${mine ? 'text-white/75' : 'text-emerald-700'}`} data-testid={`attachment-geoeffnet-${att.gridfs_id}`}>Vom Mandanten geöffnet am {new Date(att.geoeffnet_am).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}{att.geoeffnet_anzahl > 1 ? ` · ${att.geoeffnet_anzahl}× abgerufen` : ''}</p>}
            </div>
          );
        })()}
        <div
          className={`flex items-center justify-end gap-1.5 mt-0.5 text-[9.5px] ${
            mine ? 'text-white/65' : 'text-[#34536b]/65'
          }`}
        >
          <span>{fmtDateTime(item.created_at)}</span>
          {((isTeam && fromTeam) || (!isTeam && mine)) && <ReadReceipt item={item} mine={mine} isTeam={isTeam} />}
          {isTeam && onToggleUngelesen && item.author_role === 'user' && (
            <button type="button" onClick={() => onToggleUngelesen(item)} data-testid={`ungelesen-msg-${item.id}`}
              title={item.manuell_ungelesen ? 'Wieder als gelesen markieren' : 'Als ungelesen markieren (für die ganze Kanzlei)'}
              className={`inline-flex items-center gap-1 transition-colors ${item.manuell_ungelesen ? 'text-emerald-600 hover:text-[#1B3A5C]' : 'text-[#34536b]/40 hover:text-emerald-600 opacity-0 group-hover/msg:opacity-100'}`}>
              {item.manuell_ungelesen ? <><MailOpen className="w-3 h-3" /><span className="text-[9px] tracking-wider">UNGELESEN</span></> : <Mail className="w-3 h-3" />}
            </button>
          )}
          {isTeam && onMove && (item.case_id || '').startsWith('general_') && (
            <button type="button" onClick={() => onMove(item)} data-testid={`move-msg-${item.id}`} title="In Akte verschieben"
              className={`inline-flex items-center transition-colors opacity-0 group-hover/msg:opacity-100 ${mine ? 'text-white/40 hover:text-white' : 'text-[#34536b]/40 hover:text-[#1B3A5C]'}`}>
              <FolderInput className="w-3 h-3" />
            </button>
          )}
          {isTeam && (
            <button
              type="button"
              onClick={() => onToggleStar?.(item)}
              data-testid={`star-btn-${item.id}`}
              title={item.starred ? 'Stern entfernen' : 'Als wichtig markieren'}
              className={`inline-flex items-center transition-colors ${
                item.starred
                  ? (mine
                      ? 'text-amber-300 hover:text-white/70'
                      : 'text-amber-600 hover:text-[#34536b]')
                  : (mine
                      ? 'text-white/40 hover:text-amber-300 opacity-0 group-hover/msg:opacity-100'
                      : 'text-[#34536b]/40 hover:text-amber-600 opacity-0 group-hover/msg:opacity-100')
              }`}
            >
              <Star className={`w-3 h-3 ${item.starred ? 'fill-amber-400' : ''}`} />
            </button>
          )}
          {item.hidden ? (
            onUnhide && (
              <button type="button" onClick={() => onUnhide(item)} data-testid={`unhide-msg-${item.id}`} title="Nachricht wieder einblenden"
                className={`inline-flex items-center gap-1 transition-colors ${mine ? 'text-white/80 hover:text-white' : 'text-[#1B3A5C]/80 hover:text-[#1B3A5C]'}`}>
                <Eye className="w-3 h-3" /> <span className="text-[9px] tracking-wider">EINBLENDEN</span>
              </button>
            )
          ) : (
            onHide && (
              <button type="button" onClick={() => onHide(item)} data-testid={`hide-msg-${item.id}`} title="Nachricht für mich ausblenden"
                className={`inline-flex items-center transition-colors ${mine ? 'text-white/40 hover:text-white opacity-0 group-hover/msg:opacity-100' : 'text-[#34536b]/40 hover:text-[#1B3A5C] opacity-0 group-hover/msg:opacity-100'}`}>
                <EyeOff className="w-3 h-3" />
              </button>
            )
          )}
          {(isAdmin || mine) && (
            <button
              type="button"
              onClick={handleDelete}
              data-testid={`delete-msg-${item.id}`}
              title="Nachricht löschen"
              className={`inline-flex items-center transition-colors ${
                mine
                  ? 'text-white/40 hover:text-rose-300 opacity-0 group-hover/msg:opacity-100'
                  : 'text-[#34536b]/40 hover:text-rose-600 opacity-0 group-hover/msg:opacity-100'
              }`}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessageBubble;
